"use client";

import { Box, Divider, Flex } from "@chakra-ui/react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { type ReactNode, useMemo } from "react";
import { getPublicNavbarLinks } from "@/components/shared/sidebar/linkItems";
import { TenantLogo } from "@/components/shared/TenantLogo";
import { Card, Link, Text } from "@/components/ui";
import { useMediaAssetGet } from "@/features/media/hooks";
import {
  usePublicLocationsNavLink,
  usePublicTicketsNavLink,
  useTenantData,
} from "@/hooks";
import { useThemeTokens } from "@/lib/theme/tokens";
import {
  getConfiguredAdminOrigin,
  getConfiguredPortalOrigin,
} from "@/lib/utils/auth/app-origin";
import {
  getExternalStudioWebsiteUrl,
  getPublicFooterHomeHref,
  getStudioFooterAddressLines,
  getStudioFooterEmail,
  getStudioFooterName,
  getStudioMapsOpenUrl,
  getWebsiteHostnameLabel,
  partitionPublicFooterLinks,
} from "@/lib/utils/micrositeFooter";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import { PUBLIC_NAVBAR_WIDTH } from "./public-layout-constants";

const BECLASS_SITE = "https://beclassmanager.com";

function FooterHeading({ id, children }: { id: string; children: string }) {
  const theme = useThemeTokens();

  return (
    <Text
      as="p"
      id={id}
      fontSize="xs"
      fontWeight="600"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color={theme.text.tertiary}
      mb={4}
    >
      {children}
    </Text>
  );
}

function FooterNavLink({
  href,
  children,
  active = false,
  ariaLabel,
}: {
  href: string;
  children: ReactNode;
  active?: boolean;
  ariaLabel?: string;
}) {
  const theme = useThemeTokens();

  return (
    <Link
      href={href}
      aria-label={ariaLabel}
      fontSize="sm"
      fontWeight="500"
      lineHeight="1.5"
      color={active ? theme.accent.primary : theme.text.secondary}
      w="fit-content"
      maxW="100%"
      textDecoration="none"
      transition="color 0.2s ease"
      sx={{
        "@media (hover: hover)": {
          "&:hover": {
            color: theme.accent.primary,
            textDecoration: "none",
          },
        },
      }}
    >
      {children}
    </Link>
  );
}

function FooterLinkStack({
  labelledBy,
  children,
}: {
  labelledBy: string;
  children: ReactNode;
}) {
  return (
    <Flex
      as="nav"
      aria-labelledby={labelledBy}
      direction="column"
      align="flex-start"
      gap={2.5}
    >
      {children}
    </Flex>
  );
}

export const PublicFooter = () => {
  const pathname = usePathname();
  const theme = useThemeTokens();
  const { tenantData, tenantId } = useTenantData();
  const { data: session } = useSession();
  const isMember = !!session?.user?.isMember;
  const locationsNav = usePublicLocationsNavLink();
  const ticketsNav = usePublicTicketsNavLink();

  const { url: logoUrl } = useMediaAssetGet(
    {
      tenantId,
      ownerType: EntryType.Tenant,
      ownerId: tenantId,
      mediaGroup: MediaGroup.Logo,
    },
    { enabled: !!tenantId, requireAuth: false, refetchOnMount: false },
  );

  const studioName = getStudioFooterName(tenantData);
  const addressLines = getStudioFooterAddressLines(tenantData);
  const phone = tenantData?.phoneNumber?.trim() || "";
  const email = getStudioFooterEmail(tenantData?.email);
  const mapUrl = getStudioMapsOpenUrl(tenantData);
  const websiteUrl = useMemo(
    () =>
      getExternalStudioWebsiteUrl(tenantData?.siteUrl, [
        getConfiguredPortalOrigin(),
        getConfiguredAdminOrigin(),
      ]),
    [tenantData?.siteUrl],
  );

  const homeHref = getPublicFooterHomeHref();
  const year = new Date().getFullYear();

  const { explore, account, legal } = useMemo(() => {
    const links = getPublicNavbarLinks(isMember, pathname ?? "", {
      locationsNav,
      ticketsNav,
    });
    return partitionPublicFooterLinks(links);
  }, [isMember, pathname, locationsNav, ticketsNav]);

  const hasStudioIdentity = Boolean(logoUrl || studioName);
  const hasStudioContact = Boolean(
    addressLines.length || phone || email || websiteUrl,
  );
  const hasStudioBlock = hasStudioIdentity || hasStudioContact;
  const accountHeading = isMember ? "Account" : "Get started";
  const footerLabel = studioName ? `${studioName} footer` : "Site footer";

  return (
    <Flex
      as="footer"
      aria-label={footerLabel}
      w="100%"
      mx="auto"
      zIndex={1.5}
      justify="center"
      px={0}
      position="relative"
    >
      <Card
        w={PUBLIC_NAVBAR_WIDTH}
        maxW="calc(100% - 2rem)"
        mx="auto"
        mb="20px"
        px={{ base: 5, md: 7 }}
        py={{ base: 6, md: 7 }}
        borderColor={theme.borderGlass.default}
        borderWidth="1px"
        background="none"
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          align="flex-start"
          justify="space-between"
          gap={{ base: 8, md: 12 }}
        >
          {hasStudioBlock ? (
            <Box
              flex={{ md: "1.35" }}
              minW={0}
              w={{ base: "100%", md: "auto" }}
            >
              <Flex direction="column" align="flex-start" gap={4}>
                {hasStudioIdentity ? (
                  <Link
                    href={homeHref}
                    aria-label={studioName ? `${studioName} home` : "Home"}
                    display="inline-flex"
                    alignItems="center"
                    gap={3}
                    minW={0}
                    maxW="100%"
                    sx={{
                      "@media (hover: hover)": {
                        "&:hover .footer-studio-name": {
                          color: theme.accent.primary,
                        },
                      },
                    }}
                  >
                    {logoUrl ? (
                      <Box
                        flexShrink={0}
                        sx={{
                          filter:
                            theme.mode === "dark" ? "brightness(1.1)" : "none",
                        }}
                      >
                        <TenantLogo
                          width={48}
                          height={42}
                          tenantData={tenantData}
                          src={logoUrl}
                          alt=""
                          style={{
                            filter:
                              theme.mode === "dark"
                                ? "brightness(1.1)"
                                : "none",
                          }}
                        />
                      </Box>
                    ) : null}
                  </Link>
                ) : null}

                {hasStudioContact ? (
                  <Box as="address" fontStyle="normal" w="100%">
                    <Flex direction="column" align="flex-start" gap={2}>
                      {addressLines.length > 0 ? (
                        mapUrl ? (
                          <FooterNavLink
                            href={mapUrl}
                            ariaLabel={
                              studioName
                                ? `Open ${studioName} in Google Maps`
                                : "Open address in Google Maps"
                            }
                          >
                            <Box as="span" display="block">
                              {addressLines.map((line) => (
                                <Box as="span" key={line} display="block">
                                  {line}
                                </Box>
                              ))}
                            </Box>
                          </FooterNavLink>
                        ) : (
                          <Text
                            variant="secondary"
                            fontSize="sm"
                            fontWeight="500"
                            lineHeight="1.6"
                          >
                            {addressLines.map((line) => (
                              <Box as="span" key={line} display="block">
                                {line}
                              </Box>
                            ))}
                          </Text>
                        )
                      ) : null}

                      {phone ? (
                        <FooterNavLink
                          href={`tel:${phone.replace(/\s/g, "")}`}
                          ariaLabel={
                            studioName
                              ? `Call ${studioName} at ${phone}`
                              : `Call ${phone}`
                          }
                        >
                          {phone}
                        </FooterNavLink>
                      ) : null}

                      {email ? (
                        <FooterNavLink
                          href={`mailto:${email}`}
                          ariaLabel={`Email ${email}`}
                        >
                          <Box as="span" wordBreak="break-word">
                            {email}
                          </Box>
                        </FooterNavLink>
                      ) : null}

                      {websiteUrl ? (
                        <FooterNavLink
                          href={websiteUrl}
                          ariaLabel={`Visit ${getWebsiteHostnameLabel(websiteUrl)}`}
                        >
                          {getWebsiteHostnameLabel(websiteUrl)}
                        </FooterNavLink>
                      ) : null}
                    </Flex>
                  </Box>
                ) : null}
              </Flex>
            </Box>
          ) : null}

          <Flex
            flex={{ md: "1" }}
            minW={0}
            w={{ base: "100%", md: "auto" }}
            gap={{ base: 8, md: 12 }}
          >
            {explore.length > 0 ? (
              <Box flex="1" minW={0}>
                <FooterHeading id="footer-explore-heading">
                  Explore
                </FooterHeading>
                <FooterLinkStack labelledBy="footer-explore-heading">
                  {explore.map((item) => (
                    <FooterNavLink
                      key={item.href}
                      href={item.href}
                      active={item.active}
                    >
                      {item.label}
                    </FooterNavLink>
                  ))}
                </FooterLinkStack>
              </Box>
            ) : null}

            {account.length > 0 ? (
              <Box flex="1" minW={0}>
                <FooterHeading id="footer-account-heading">
                  {accountHeading}
                </FooterHeading>
                <FooterLinkStack labelledBy="footer-account-heading">
                  {account.map((item) => (
                    <FooterNavLink
                      key={item.href}
                      href={item.href}
                      active={item.active}
                    >
                      {item.label}
                    </FooterNavLink>
                  ))}
                </FooterLinkStack>
              </Box>
            ) : null}
          </Flex>
        </Flex>

        <Divider
          my={{ base: 6, md: 7 }}
          borderColor={theme.borderGlass.subtle}
          opacity={0.8}
        />

        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "flex-start", md: "center" }}
          justify="space-between"
          gap={{ base: 3, md: 6 }}
        >
          <Flex
            align="center"
            gap={2}
            flexWrap="wrap"
            rowGap={1}
            fontSize="xs"
            lineHeight="1.5"
          >
            <Text variant="tertiary" fontSize="xs">
              &copy; {year}
              {studioName ? ` ${studioName}` : ""}
            </Text>
            {legal.map((item) => (
              <Flex key={item.href} align="center" gap={2}>
                <Text variant="muted" fontSize="xs" aria-hidden>
                  ·
                </Text>
                <Link
                  href={item.href}
                  fontSize="xs"
                  fontWeight="500"
                  color={
                    item.active ? theme.accent.primary : theme.text.tertiary
                  }
                  textDecoration="none"
                  transition="color 0.2s ease"
                  sx={{
                    "@media (hover: hover)": {
                      "&:hover": {
                        color: theme.accent.primary,
                        textDecoration: "none",
                      },
                    },
                  }}
                >
                  {item.label}
                </Link>
              </Flex>
            ))}
          </Flex>

          <Text variant="tertiary" fontSize="xs" lineHeight="1.5">
            Powered by{" "}
            <Link
              href={BECLASS_SITE}
              external
              color={theme.accent.primary}
              fontWeight="500"
              sx={{
                "@media (hover: hover)": {
                  "&:hover": {
                    textDecoration: "underline",
                  },
                },
              }}
            >
              BeClass Manager
            </Link>
          </Text>
        </Flex>
      </Card>
    </Flex>
  );
};
